import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppFormField, AppInput } from '../ui';
import { notify } from '../common/AppToast';
import MediaPlanningFilters, { StatutPhasePicker } from './KkpCronMediaPlanningFilters';
import KkpCronCommerciauxLundiFilters from './KkpCronCommerciauxLundiFilters';
import KkpCronAlertCard from './KkpCronAlertCard';
import {
  CANAUX_OPTIONS,
  DEFAULT_PENSE_BETE_MESSAGE,
  DEFAULT_RELANCE_MONTH_DAYS,
  HOUR_PRESETS,
  MONTH_DAY_OPTIONS,
  SCHEDULE_PRESETS_MONTH,
  SCHEDULE_PRESETS_WEEKLY,
  WEEKDAY_OPTIONS,
  WIZARD_STEPS,
  buildAutoName,
  defaultCommerciauxLundiStatuts,
  defaultCronName,
  emptyForm,
  findOverlappingDuplicates,
  formatCommerciauxLundiFiltersPhrase,
  formatMediaPlanningFiltersPhrase,
  formatFormRecipientNames,
  formatRecipientsPhrase,
  formatSchedulePhrase,
  getCronTypeUi,
  hasCronTestMode,
  isInstantCronType,
  isInstantRecipientsCronType,
  isSystemCronType,
  matchMonthPreset,
  matchWeeklyPreset,
  normalizeWeekAnchor,
  normalizeWeekInterval,
  weekAnchorMatchesWeekdays,
  weekAnchorWeekday,
} from '../../utils/kkpCronUi';

const CronTypePicker = ({ cronTypes, value, onSelect }) => (
  <div className="kkp-crons-type-grid">
    {(cronTypes || []).filter((type) => (
      (!type.system && !type.instant && !isInstantCronType(type.id)) || type.id === value
    )).map((type) => {
      const ui = getCronTypeUi(type.id);
      const selected = value === type.id;
      return (
        <button
          key={type.id}
          type="button"
          className={`kkp-crons-type-card ${selected ? 'is-selected' : ''}`}
          onClick={() => onSelect(type.id)}
        >
          <span className="kkp-crons-type-card__icon" style={{ backgroundColor: `${ui.color}18`, color: ui.color }}>
            <Icon icon={ui.icon} width={26} />
          </span>
          <span className="kkp-crons-type-card__text">
            <span className="kkp-crons-type-card__label">{type.label}</span>
            <span className="kkp-crons-type-card__blurb">{ui.blurb || type.description || 'Mail automatique'}</span>
          </span>
          <span className="kkp-crons-type-card__go">
            {selected ? 'Choisi' : 'Choisir'}
            <Icon icon="mdi:chevron-right" width={18} />
          </span>
        </button>
      );
    })}
  </div>
);

const CronPersonPicker = ({
  items,
  selectedIds,
  onToggle,
  emptyLabel = 'Personne n’est disponible.',
}) => {
  const selected = (selectedIds || []).map(Number);
  const list = [...(items || [])].sort((a, b) =>
    String(a.label || '').localeCompare(String(b.label || ''), 'fr', { sensitivity: 'base' }),
  );

  if (list.length === 0) {
    return <span className="text-sm text-secondary-light">{emptyLabel}</span>;
  }

  return (
    <div className="kkp-crons-people-grid">
      {list.map((item) => {
        const id = Number(item.id);
        const checked = selected.includes(id);
        return (
          <button
            key={item.id}
            type="button"
            className={`kkp-crons-person ${checked ? 'is-selected' : ''}`}
            onClick={() => onToggle(id)}
            title={item.email || item.label}
          >
            <span className={`kkp-crons-person__check ${checked ? 'is-on' : ''}`} aria-hidden>
              {checked ? <Icon icon="mdi:check" width={16} /> : null}
            </span>
            <span className="kkp-crons-person__text">
              <span className="kkp-crons-person__name">{item.label}</span>
              {item.email ? <small>{item.email}</small> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const ExtraEmailsField = ({ value, onChange, placeholder }) => (
  <input
    className="form-control"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
  />
);

const CronChoiceGrid = ({ items, isSelected, onSelect, variant = 'default' }) => (
  <div className={`kkp-crons-choice-grid ${variant !== 'default' ? `is-${variant}` : ''}`}>
    {items.map((item) => {
      const selected = isSelected(item);
      return (
        <button
          key={item.id}
          type="button"
          className={`kkp-crons-choice ${selected ? 'is-selected' : ''}`}
          onClick={() => onSelect(item)}
        >
          <span className={`kkp-crons-choice__check ${selected ? 'is-on' : ''}`} aria-hidden>
            {selected ? <Icon icon="mdi:check" width={16} /> : null}
          </span>
          <span className="kkp-crons-choice__label">{item.label}</span>
        </button>
      );
    })}
  </div>
);

const FIXED_STATUTS_BY_TYPE = {
  mails_clients_mag_set: ['Vendu', 'Vendu OK', 'Créa Client'],
  pub_graphiste: ['Vendu OK'],
  relance_vente_n1: ['Vendu', 'Vendu OK', 'Créa Client', 'BAT Envoyé', 'BAT Validé'],
};

const PREVIEW_COPY_COUNT = 4;

const ExistingAlertsPanel = ({ crons, cronTypes, commerciaux, onDuplicate }) => {
  const list = (crons || []).filter((cron) => !isSystemCronType(cron.cron_type) && !isInstantCronType(cron.cron_type));
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (list.length === 0) return null;

  const visible = showAll ? list : list.slice(0, PREVIEW_COPY_COUNT);
  const hiddenCount = list.length - visible.length;

  return (
    <div className={`kkp-crons-copy ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="kkp-crons-copy__toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="kkp-crons-copy__icon">
          <Icon icon="mdi:content-copy" width={18} />
        </span>
        <span className="kkp-crons-copy__text">
          Copier une alerte existante
          <span className="kkp-crons-copy__count">{list.length}</span>
        </span>
        <Icon icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={20} />
      </button>
      {open && (
        <div className="kkp-crons-copy__body">
          <div className="kkp-crons-copy__grid">
            {visible.map((cron) => (
              <KkpCronAlertCard
                key={cron.id}
                cron={cron}
                cronTypes={cronTypes}
                commerciaux={commerciaux}
                showType
                variant="duplicate"
                onDuplicate={onDuplicate}
              />
            ))}
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              className="kkp-crons-copy__more"
              onClick={() => setShowAll(true)}
            >
              Voir les {hiddenCount} autres
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const StepIntro = ({ question, hint }) => (
  <div className="kkp-crons-step-intro">
    <h3>{question}</h3>
    {hint ? <p>{hint}</p> : null}
  </div>
);

const KkpCronsWizard = ({
  form,
  setForm,
  cronTypes,
  users,
  commerciaux,
  meta,
  editingId,
  saving,
  nameTouched,
  setNameTouched,
  wizardStep,
  setWizardStep,
  duplicateCrons,
  currentUserEmail,
  onSubmit,
  onCancel,
  onEditDuplicate,
  existingCrons = [],
  onDuplicate,
}) => {
  const isMediaPlanning = form.cron_type === 'media_planning';
  const isCommerciauxLundi = form.cron_type === 'commerciaux_lundi';
  const isKlubEtapesLundi = form.cron_type === 'klub_etapes_lundi';
  const isPubGraphiste = form.cron_type === 'pub_graphiste';
  const isMailsClientsMagSet = form.cron_type === 'mails_clients_mag_set';
  const isRelanceVenteN1 = form.cron_type === 'relance_vente_n1';
  const isSystemCron = isSystemCronType(form.cron_type);
  const isInstantRecipients = isInstantRecipientsCronType(form.cron_type);
  const isDayOfMonthCron = isMailsClientsMagSet;
  const isPenseBeteCodir = form.cron_type === 'pense_bete_codir';
  const overlapping = useMemo(
    () => findOverlappingDuplicates(duplicateCrons, form),
    [duplicateCrons, form],
  );
  const statutOptions = useMemo(() => {
    if (!meta?.statuts) return [];
    return meta.statuts
      .map((s) => {
        if (typeof s === 'string') return { value: s, label: s, couleur: null };
        const value = s.value || s.label || s.name || '';
        if (!value) return null;
        return {
          value,
          label: s.label || value,
          couleur: s.couleur || null,
        };
      })
      .filter(Boolean);
  }, [meta?.statuts]);
  const fixedStatuts = FIXED_STATUTS_BY_TYPE[form.cron_type] || null;
  const [customSchedule, setCustomSchedule] = useState(false);
  const defaultLundiStatuts = useMemo(
    () => defaultCommerciauxLundiStatuts(meta?.statuts),
    [meta?.statuts],
  );

  useEffect(() => {
    if (!isCommerciauxLundi || defaultLundiStatuts.length === 0) return;
    setForm((prev) => {
      if ((prev.filters?.statut_phase || []).length > 0) return prev;
      return { ...prev, filters: { ...prev.filters, statut_phase: defaultLundiStatuts } };
    });
  }, [defaultLundiStatuts, isCommerciauxLundi, setForm]);

  useEffect(() => {
    if (nameTouched || editingId) return;
    const auto = buildAutoName(form, users, cronTypes, commerciaux);
    if (auto && auto !== form.name) {
      setForm((prev) => (prev.name === auto ? prev : { ...prev, name: auto }));
    }
  }, [
    commerciaux,
    cronTypes,
    editingId,
    form.cron_type,
    form.extra_emails,
    form.filters,
    form.hour,
    form.minute,
    form.name,
    form.recipient_user_ids,
    form.weekdays,
    nameTouched,
    setForm,
    users,
  ]);

  useEffect(() => {
    setCustomSchedule(false);
  }, [form.cron_type]);

  const selectType = (nextType) => {
    if (nextType === form.cron_type) {
      setWizardStep(1);
      return;
    }
    const nextDefaultName = defaultCronName(nextType, cronTypes);
    setForm((prev) => ({
      ...emptyForm(nextType, cronTypes, {
        testEmail: currentUserEmail,
        defaultTestMode: !editingId,
      }),
      name: nameTouched ? prev.name : nextDefaultName,
    }));
    setWizardStep(1);
  };

  const toggleWeekday = (day) => {
    const value = Number(day);
    setForm((prev) => {
      const current = (prev.weekdays || []).map(Number);
      const next = current.includes(value)
        ? current.filter((d) => d !== value)
        : [...current, value];
      if (next.length === 0) {
        notify.error('Choisissez au moins un jour.');
        return prev;
      }
      const sorted = [...next].sort((a, b) => a - b);
      return {
        ...prev,
        weekdays: sorted,
        weekday: sorted[0],
        filters: { ...prev.filters, weekdays: sorted },
      };
    });
  };

  const toggleMonthDay = (day) => {
    const value = Number(day);
    setForm((prev) => {
      const current = (prev.filters?.month_days || DEFAULT_RELANCE_MONTH_DAYS).map(Number);
      const next = current.includes(value)
        ? current.filter((d) => d !== value)
        : [...current, value];
      if (next.length === 0) {
        notify.error('Choisissez au moins un jour du mois.');
        return prev;
      }
      const sorted = [...next].sort((a, b) => a - b);
      return {
        ...prev,
        filters: { ...prev.filters, month_days: sorted },
      };
    });
  };

  const applyWeeklyPreset = (preset) => {
    if (!preset.weekdays) return;
    setForm((prev) => ({
      ...prev,
      weekdays: [...preset.weekdays],
      weekday: preset.weekdays[0],
      hour: preset.hour,
      minute: preset.minute,
      filters: { ...prev.filters, weekdays: [...preset.weekdays] },
    }));
  };

  const applyMonthPreset = (preset) => {
    if (!preset.monthDays) return;
    setForm((prev) => ({
      ...prev,
      filters: { ...prev.filters, month_days: [...preset.monthDays] },
    }));
  };

  const setTime = (hour, minute) => {
    setForm((prev) => ({ ...prev, hour, minute }));
  };

  const setWeekInterval = (interval) => {
    setForm((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        week_interval: normalizeWeekInterval(interval),
      },
    }));
  };

  const setWeekAnchor = (value) => {
    setForm((prev) => ({
      ...prev,
      filters: { ...prev.filters, week_anchor: value },
    }));
  };

  const weeklyPresetId = customSchedule ? 'custom' : matchWeeklyPreset(form.weekdays);
  const monthPresetId = customSchedule ? 'custom' : matchMonthPreset(form.filters?.month_days);
  const timeValue = `${String(form.hour).padStart(2, '0')}:${String(form.minute).padStart(2, '0')}`;
  const matchedHourPreset = HOUR_PRESETS.find((preset) => (
    preset.hour === Number(form.hour) && preset.minute === Number(form.minute)
  ));

  const canGoNext = () => {
    if (wizardStep === 0) return Boolean(form.cron_type);
    if (wizardStep === 1) {
      if (isSystemCron || isDayOfMonthCron) return true;
      if (isRelanceVenteN1) return (form.filters?.month_days || []).length > 0;
      if ((form.weekdays || []).length === 0) return false;
      if (normalizeWeekInterval(form.filters?.week_interval) === 2) {
        return weekAnchorMatchesWeekdays(form.filters?.week_anchor, form.weekdays);
      }
      return true;
    }
    if (wizardStep === 2 && isMediaPlanning) {
      return (form.filters?.statut_phase || []).length > 0;
    }
    if (wizardStep === 2 && isCommerciauxLundi) {
      const canaux = form.filters?.canaux || [];
      if (canaux.length === 0) return false;
      if (form.filters?.period_mode === 'custom' && (canaux.includes('mag') || canaux.includes('set'))) {
        return (form.filters?.mois || []).length > 0;
      }
      return true;
    }
    if (wizardStep === 3) {
      if (!isCommerciauxLundi) return true;
      return (form.filters?.statut_phase || []).length > 0;
    }
    return true;
  };

  const recapSchedule = formatSchedulePhrase({
    cronType: form.cron_type,
    weekdays: form.weekdays,
    hour: form.hour,
    minute: form.minute,
    monthDays: form.filters?.month_days,
    weekInterval: form.filters?.week_interval,
    weekAnchor: form.filters?.week_anchor,
  });
  const recapNames = formatFormRecipientNames(form, users, commerciaux);
  const recapSentence = isInstantRecipients
    ? (recapNames.length === 0
      ? 'Aucun destinataire pour l’instant : le mail ne partira pas.'
      : recapNames.length === 1
        ? `${formatRecipientsPhrase(recapNames)} recevra ce mail dès le changement de statut.`
        : `${formatRecipientsPhrase(recapNames)} recevront ce mail dès le changement de statut.`)
    : isSystemCron
    ? `${recapSchedule}. Mise à jour interne, sans mail.`
    : recapNames.length === 0
      ? `${recapSchedule}. Il manque encore le destinataire.`
      : recapNames.length === 1
        ? `${recapSchedule}, ${formatRecipientsPhrase(recapNames)} recevra ce mail.`
        : `${recapSchedule}, ${formatRecipientsPhrase(recapNames)} recevront ce mail.`;
  const recapFilters = isCommerciauxLundi
    ? formatCommerciauxLundiFiltersPhrase(form.filters)
    : (isMediaPlanning ? formatMediaPlanningFiltersPhrase(form.filters) : '');

  const needsStatutsStep = !isSystemCron && !isInstantRecipients && (isCommerciauxLundi || Boolean(fixedStatuts));
  const visibleSteps = (() => {
    if (isInstantRecipients) {
      return [{
        id: 'qui',
        title: 'Destinataire',
        question: 'Qui doit recevoir ce mail ?',
        hint: 'Le mail part dès que le statut change, vers ces personnes.',
      }];
    }
    const steps = isSystemCron
      ? [
        { id: 'quoi', title: 'Tâche', question: 'Quelle tâche voulez-vous régler ?', hint: 'Cliquez sur la tâche, puis choisissez l’heure.' },
        { ...WIZARD_STEPS[1] },
      ]
      : (needsStatutsStep ? WIZARD_STEPS : WIZARD_STEPS.filter((step) => step.id !== 'statuts'));
    if (isKlubEtapesLundi) {
      return steps.map((step) => (
        step.id === 'qui'
          ? {
            ...step,
            question: 'Qui doit recevoir une copie ?',
            hint: 'Chaque responsable reçoit ses étapes. Les personnes ci-dessous sont en copie, c’est facultatif.',
          }
          : step
      ));
    }
    if (!isCommerciauxLundi) return steps;
    return steps.map((step) => (
      step.id === 'statuts'
        ? { ...step, hint: 'Par défaut : tout sauf Option, Rien et BAT Validé. Vous pouvez en ajouter ou en retirer.' }
        : step
    ));
  })();
  const lastStep = visibleSteps.length - 1;
  const currentStepMeta = visibleSteps[Math.min(wizardStep, lastStep)] || visibleSteps[0];
  const isCopied = !editingId && /\s\(\d+\)\s*$/.test(String(form.name || ''));

  useEffect(() => {
    if (editingId) return;
    if ((wizardStep === 1 || wizardStep === 2) && !isCopied && duplicateCrons?.length) {
      const firstName = duplicateCrons[0]?.name || '';
      const message = duplicateCrons.length === 1
        ? `Une alerte de ce type existe déjà : « ${firstName} ».`
        : `${duplicateCrons.length} alertes de ce type existent déjà.`;
      notify.warning(message, { toastId: `cron-type-exists-${form.cron_type}` });
    }
    if (wizardStep === 2 && overlapping?.length) {
      notify.warning('Attention : une personne choisie reçoit déjà ce mail.', {
        toastId: `cron-overlap-${form.cron_type}`,
      });
    }
  }, [duplicateCrons, editingId, form.cron_type, isCopied, overlapping, wizardStep]);

  useEffect(() => {
    if (wizardStep > lastStep) setWizardStep(lastStep);
  }, [lastStep, setWizardStep, wizardStep]);

  const recapBlock = (
    <div className="kkp-crons-recap">
      <Icon icon="mdi:check-decagram-outline" width={22} />
      <div>
        <strong>En résumé</strong>
        <p className="mb-0">
          {recapSentence}
          {recapFilters ? <> Dossiers : {recapFilters}.</> : null}
          {form.filters?.test_mode ? (
            <> Pour l’instant, seul <strong>{form.filters?.test_email || 'votre adresse'}</strong> recevra le mail (mode essai).</>
          ) : null}
        </p>
      </div>
    </div>
  );

  const nameField = (
    <section className="kkp-crons-panel">
      <p className="kkp-crons-panel__label">Nom de l’alerte</p>
      <input
        className="form-control"
        required
        value={form.name}
        onChange={(e) => {
          setNameTouched(true);
          setForm((f) => ({ ...f, name: e.target.value }));
        }}
        placeholder={defaultCronName(form.cron_type, cronTypes)}
      />
    </section>
  );

  const handleWizardSubmit = (event) => {
    event.preventDefault();
    if (wizardStep < lastStep) {
      if (canGoNext()) setWizardStep((step) => Math.min(lastStep, step + 1));
      return;
    }
    if (!canGoNext()) return;
    onSubmit(event);
  };

  return (
    <form onSubmit={handleWizardSubmit} className="kkp-crons-wizard">
      <div className="kkp-crons-wizard__head">
        <div>
          <p className="kkp-crons-wizard__title">
            {isInstantRecipients
              ? 'Choisir les destinataires'
              : editingId
                ? (isSystemCron ? 'Modifier cette tâche' : 'Modifier cette alerte')
                : 'Créer une alerte email'}
          </p>
          {editingId ? (
            <p className="kkp-crons-wizard__sub">
              Changez seulement ce qui doit changer, puis enregistrez.
            </p>
          ) : null}
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
          Annuler
        </button>
      </div>

      <div className="kkp-crons-wizard__steps" role="tablist" aria-label="Étapes">
        {visibleSteps.map((step, index) => {
          const isActive = index === wizardStep;
          const isDone = index < wizardStep;
          return (
            <button
              key={step.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`kkp-crons-wizard__step ${isActive ? 'is-active' : ''} ${isDone ? 'is-done' : ''}`}
              onClick={() => {
                if (index <= wizardStep || (index === 1 && form.cron_type) || editingId) {
                  setWizardStep(index);
                }
              }}
            >
              <span className="kkp-crons-wizard__num">{index + 1}</span>
              <span className="kkp-crons-wizard__step-label">{step.title}</span>
            </button>
          );
        })}
      </div>

      <div className="kkp-crons-wizard__body">
        <StepIntro question={currentStepMeta.question} hint={currentStepMeta.hint} />
        {wizardStep === 0 && !editingId && !isInstantRecipients && (
          <ExistingAlertsPanel
            crons={existingCrons}
            cronTypes={cronTypes}
            commerciaux={commerciaux}
            onDuplicate={onDuplicate}
          />
        )}
        {wizardStep === 0 && !editingId && !isInstantRecipients && (existingCrons || []).some((cron) => !isSystemCronType(cron.cron_type) && !isInstantCronType(cron.cron_type)) && (
          <p className="kkp-crons-or">Nouvelle alerte</p>
        )}
        {wizardStep === 0 && !isInstantRecipients && (
          <CronTypePicker
            cronTypes={cronTypes}
            value={(editingId || isCopied) ? form.cron_type : ''}
            onSelect={selectType}
          />
        )}

        {wizardStep === 1 && (
          <div className="kkp-crons-step">
            <div className="kkp-crons-when">
              <section className="kkp-crons-panel">
                {isSystemCron ? (
                  <div className="kkp-crons-help mb-0">
                    <Icon icon="mdi:calendar-clock" width={18} />
                    <div>
                      <strong>Tous les jours</strong>
                      <p className="mb-0">
                        La synchro tourne chaque matin. Choisissez seulement l’heure.
                      </p>
                    </div>
                  </div>
                ) : isDayOfMonthCron ? (
                  <div className="kkp-crons-help mb-0">
                    <Icon icon="mdi:calendar-clock" width={18} />
                    <div>
                      <strong>Dates déjà prévues</strong>
                      <p className="mb-0">
                        1er du mois, rappel le 15, et 7 jours avant une date butoir.
                        Choisissez seulement l’heure.
                      </p>
                    </div>
                  </div>
                ) : isRelanceVenteN1 ? (
                  <>
                    <p className="kkp-crons-panel__label">Jour du mois</p>
                    <CronChoiceGrid
                      items={SCHEDULE_PRESETS_MONTH.map((preset) => ({
                        id: preset.id,
                        label: preset.label,
                        preset,
                      }))}
                      isSelected={(item) => monthPresetId === item.id}
                      onSelect={(item) => {
                        if (item.preset.monthDays) {
                          setCustomSchedule(false);
                          applyMonthPreset(item.preset);
                          return;
                        }
                        setCustomSchedule(true);
                      }}
                    />
                    {monthPresetId === 'custom' && (
                      <div className="mt-2">
                        <CronChoiceGrid
                          variant="days"
                          items={MONTH_DAY_OPTIONS.map((day) => ({
                            id: day,
                            label: String(day),
                          }))}
                          isSelected={(item) => (form.filters?.month_days || DEFAULT_RELANCE_MONTH_DAYS)
                            .map(Number)
                            .includes(item.id)}
                          onSelect={(item) => toggleMonthDay(item.id)}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="kkp-crons-panel__label">Jour</p>
                    <CronChoiceGrid
                      items={SCHEDULE_PRESETS_WEEKLY.map((preset) => ({
                        id: preset.id,
                        label: preset.label,
                        preset,
                      }))}
                      isSelected={(item) => weeklyPresetId === item.id}
                      onSelect={(item) => {
                        if (item.preset.weekdays) {
                          setCustomSchedule(false);
                          applyWeeklyPreset(item.preset);
                          return;
                        }
                        setCustomSchedule(true);
                      }}
                    />
                    {weeklyPresetId === 'custom' && (
                      <div className="mt-2">
                        <CronChoiceGrid
                          items={WEEKDAY_OPTIONS.map((day) => ({
                            id: day.value,
                            label: day.label,
                          }))}
                          isSelected={(item) => (form.weekdays || []).map(Number).includes(item.id)}
                          onSelect={(item) => toggleWeekday(item.id)}
                        />
                      </div>
                    )}
                    <p className="kkp-crons-panel__label mt-3">Fréquence</p>
                    <CronChoiceGrid
                      items={[
                        { id: 1, label: 'Chaque semaine' },
                        { id: 2, label: 'Une semaine sur deux' },
                      ]}
                      isSelected={(item) => normalizeWeekInterval(form.filters?.week_interval) === item.id}
                      onSelect={(item) => setWeekInterval(item.id)}
                    />
                    {normalizeWeekInterval(form.filters?.week_interval) === 2 && (
                      <AppFormField
                        className="mt-2 mb-0"
                        label="Premier envoi"
                        htmlFor="kkp-cron-week-anchor"
                        required
                        hint="Le mail repartira ce jour-là, puis tous les 14 jours."
                        error={(() => {
                          const anchor = normalizeWeekAnchor(form.filters?.week_anchor);
                          if (!form.filters?.week_anchor || !anchor) return '';
                          if (weekAnchorMatchesWeekdays(anchor, form.weekdays)) return '';
                          const weekday = weekAnchorWeekday(anchor);
                          const label = WEEKDAY_OPTIONS.find((day) => day.value === weekday)?.label || 'ce jour';
                          return `Cette date tombe un ${label.toLowerCase()}. Choisissez un jour déjà coché.`;
                        })()}
                      >
                        <AppInput
                          id="kkp-cron-week-anchor"
                          type="date"
                          required
                          value={form.filters?.week_anchor || ''}
                          onChange={(e) => setWeekAnchor(e.target.value)}
                        />
                      </AppFormField>
                    )}
                  </>
                )}
              </section>

              <section className="kkp-crons-panel">
                <p className="kkp-crons-panel__label">Heure</p>
                <CronChoiceGrid
                  variant="hours"
                  items={HOUR_PRESETS.map((preset) => ({
                    id: preset.label,
                    label: preset.label,
                    hour: preset.hour,
                    minute: preset.minute,
                  }))}
                  isSelected={(item) => matchedHourPreset?.label === item.label}
                  onSelect={(item) => setTime(item.hour, item.minute)}
                />
                <div className={`kkp-crons-choice kkp-crons-choice--time ${matchedHourPreset ? '' : 'is-selected'}`}>
                  <span className="kkp-crons-choice__label">Autre heure</span>
                  <input
                    type="time"
                    className="form-control kkp-crons-hour-input"
                    required
                    value={timeValue}
                    onChange={(e) => {
                      const [h, m] = (e.target.value || '09:00').split(':').map((n) => Number(n));
                      setTime(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0);
                    }}
                  />
                </div>
              </section>
            </div>
            {nameField}
            {wizardStep === lastStep && recapBlock}
          </div>
        )}

        {(wizardStep === 2 || isInstantRecipients) && (
          <div className="kkp-crons-step">
            {isKlubEtapesLundi && (
              <div className="kkp-crons-help mb-0">
                <Icon icon="mdi:information-outline" width={18} />
                <div className="text-sm">
                  Chaque responsable reçoit uniquement ses étapes ouvertes sur les sites Klub
                  {' '}<strong>en cours</strong>. Un lien du mail ouvre une page pour valider l’étape.
                  Les personnes ci-dessous reçoivent une copie.
                </div>
              </div>
            )}

            {isCommerciauxLundi && (
              <section className="kkp-crons-panel">
                <div className="kkp-crons-panel__head">
                  <p className="kkp-crons-panel__label mb-0">Commerciaux</p>
                  <span className="badge bg-primary-100 text-primary-600">
                    {(form.filters?.commercial_ids || []).length === 0
                      ? 'Tous'
                      : `${(form.filters?.commercial_ids || []).length} choisi${(form.filters?.commercial_ids || []).length > 1 ? 's' : ''}`}
                  </span>
                </div>
                <CronPersonPicker
                  items={(commerciaux || []).map((c) => ({
                    id: c.id,
                    label: c.nom || c.name || c.label || c.fullName || `Commercial #${c.id}`,
                    email: c.email || '',
                  }))}
                  selectedIds={form.filters?.commercial_ids || []}
                  emptyLabel="Aucun commercial."
                  onToggle={(id) => {
                    setForm((prev) => {
                      const current = (prev.filters?.commercial_ids || []).map(Number);
                      const next = current.includes(id)
                        ? current.filter((x) => x !== id)
                        : [...current, id];
                      return {
                        ...prev,
                        filters: { ...prev.filters, commercial_ids: next },
                      };
                    });
                  }}
                />
                {(form.filters?.commercial_ids || []).length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link px-0 mt-1"
                    onClick={() => setForm((f) => ({
                      ...f,
                      filters: { ...f.filters, commercial_ids: [] },
                    }))}
                  >
                    Tout le monde (= tous)
                  </button>
                )}
              </section>
            )}

            {isRelanceVenteN1 && (
              <>
                <section className="kkp-crons-panel">
                  <p className="kkp-crons-panel__label">Supports</p>
                  <CronChoiceGrid
                    items={CANAUX_OPTIONS.map((canal) => ({
                      id: canal.id,
                      label: canal.label,
                    }))}
                    isSelected={(item) => (form.filters?.canaux || []).includes(item.id)}
                    onSelect={(item) => {
                      setForm((prev) => {
                        const current = prev.filters?.canaux || CANAUX_OPTIONS.map((c) => c.id);
                        const next = current.includes(item.id)
                          ? current.filter((id) => id !== item.id)
                          : [...current, item.id];
                        if (next.length === 0) {
                          notify.error('Choisissez au moins un support.');
                          return prev;
                        }
                        return {
                          ...prev,
                          filters: { ...prev.filters, canaux: next },
                        };
                      });
                    }}
                  />
                </section>
                <div className="kkp-crons-help mb-0">
                  <Icon icon="mdi:information-outline" width={18} />
                  <div className="text-sm">
                    On regarde les clients parus en N-1 sur <strong>M+2</strong> (septembre → novembre).
                    Dès que la <strong>date butoir</strong> du mois en cours est dépassée, ça passe à
                    {' '}<strong>M+3</strong> sans attendre la fin du mois.
                  </div>
                </div>
              </>
            )}

            {isMailsClientsMagSet && (
              <div className="kkp-crons-help mb-0">
                <Icon icon="mdi:information-outline" width={18} />
                <div className="text-sm">
                  Dossiers MAG / SET en <strong>Vendu</strong>, <strong>Vendu OK</strong> et <strong>Créa Client</strong>.
                  Le récap part aux personnes ci-dessous. L’envoi aux clients n’est pas encore activé.
                </div>
              </div>
            )}

            {isPenseBeteCodir && (
              <section className="kkp-crons-panel">
                <p className="kkp-crons-panel__label">Message</p>
                <textarea
                  className="form-control"
                  rows={5}
                  required
                  value={form.filters?.message_body || ''}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    filters: { ...f.filters, message_body: e.target.value },
                  }))}
                  placeholder={DEFAULT_PENSE_BETE_MESSAGE}
                />
              </section>
            )}

            <div className="kkp-crons-who">
              <section className="kkp-crons-panel">
                <div className="kkp-crons-panel__head">
                  <p className="kkp-crons-panel__label mb-0">
                    {isCommerciauxLundi || isKlubEtapesLundi ? 'Copie (facultatif)' : 'Destinataire'}
                  </p>
                  <span className="badge bg-primary-100 text-primary-600">
                    {form.recipient_user_ids.length === 0
                      ? 'Personne pour l’instant'
                      : `${form.recipient_user_ids.length} personne${form.recipient_user_ids.length > 1 ? 's' : ''}`}
                  </span>
                </div>
                <CronPersonPicker
                  items={users.map((u) => ({
                    id: u.id,
                    label: u.fullName,
                    email: u.email || '',
                  }))}
                  selectedIds={form.recipient_user_ids}
                  emptyLabel="Aucun compte disponible."
                  onToggle={(id) => {
                    setForm((prev) => {
                      const current = (prev.recipient_user_ids ?? []).map(Number);
                      const ids = current.includes(id)
                        ? current.filter((x) => x !== id)
                        : [...current, id];
                      return { ...prev, recipient_user_ids: ids };
                    });
                  }}
                />
              </section>

              <section className="kkp-crons-panel">
                <p className="kkp-crons-panel__label">Autre destinataire (e-mail)</p>
                <ExtraEmailsField
                  value={form.extra_emails}
                  onChange={(next) => setForm((f) => ({ ...f, extra_emails: next }))}
                  placeholder={
                    isCommerciauxLundi || isKlubEtapesLundi
                      ? 'autre@exemple.com'
                      : isMailsClientsMagSet || isRelanceVenteN1
                        ? 'client@exemple.com'
                        : isPenseBeteCodir
                          ? 'codir@exemple.com'
                          : isPubGraphiste
                            ? 'cv@iti-conseil.com'
                            : 'commercial@exemple.com'
                  }
                />
              </section>
            </div>

            {hasCronTestMode(form.cron_type) && (
              <div className={`kkp-crons-test ${form.filters?.test_mode ? 'is-on' : ''}`}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <label className="form-label mb-0 fw-semibold" htmlFor="cron-test-mode">
                    <Icon icon="mdi:flask-outline" className="me-1" width={16} />
                    Envoyer un essai d’abord (recommandé)
                  </label>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="cron-test-mode"
                      checked={Boolean(form.filters?.test_mode)}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        filters: {
                          ...f.filters,
                          test_mode: e.target.checked,
                          test_email: e.target.checked
                            ? (f.filters?.test_email || currentUserEmail || '')
                            : f.filters?.test_email,
                        },
                      }))}
                    />
                  </div>
                </div>
                <p className="text-sm mb-0">
                  Le mail part seulement à l’adresse ci-dessous, pas aux vrais destinataires.
                  Décochez cette case quand tout est bon.
                </p>
                {Boolean(form.filters?.test_mode) && (
                  <div className="mt-2">
                    <label className="form-label">Adresse de l’essai</label>
                    <input
                      className="form-control"
                      type="email"
                      required
                      value={form.filters?.test_email || ''}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        filters: { ...f.filters, test_email: e.target.value },
                      }))}
                      placeholder={currentUserEmail || 'vous@exemple.com'}
                    />
                  </div>
                )}
              </div>
            )}

            {isCommerciauxLundi && (
              <section className="kkp-crons-panel">
                <p className="kkp-crons-panel__label">Quels dossiers inclure ?</p>
                <KkpCronCommerciauxLundiFilters
                  filters={form.filters}
                  onChange={(next) => setForm((prev) => ({ ...prev, filters: next }))}
                />
              </section>
            )}

            {isMediaPlanning && (
              <section className="kkp-crons-panel">
                <p className="kkp-crons-panel__label">Quels dossiers inclure ?</p>
                <MediaPlanningFilters
                  filters={form.filters}
                  onChange={(next) => setForm((prev) => ({ ...prev, filters: next }))}
                  meta={meta}
                  commerciaux={commerciaux}
                />
              </section>
            )}
            {wizardStep === lastStep && recapBlock}
          </div>
        )}

        {wizardStep === 3 && (
          <div className="kkp-crons-step">
            {isCommerciauxLundi && (
              <section className="kkp-crons-panel">
                <StatutPhasePicker
                  options={statutOptions}
                  selected={form.filters?.statut_phase || []}
                  onToggle={(value) => {
                    setForm((prev) => {
                      const current = prev.filters?.statut_phase || [];
                      const next = current.includes(value)
                        ? current.filter((item) => item !== value)
                        : [...current, value];
                      return { ...prev, filters: { ...prev.filters, statut_phase: next } };
                    });
                  }}
                  onClear={() => setForm((prev) => ({
                    ...prev,
                    filters: { ...prev.filters, statut_phase: defaultLundiStatuts },
                  }))}
                  hint="Par défaut : tout sauf Option, Rien et BAT Validé. Cliquez pour ajouter ou retirer un statut."
                  clearLabel="Par défaut"
                />
              </section>
            )}
            {fixedStatuts && (
              <section className="kkp-crons-panel">
                <StatutPhasePicker
                  options={fixedStatuts.map((value) => (
                    statutOptions.find((s) => s.value === value) || { value, label: value, couleur: null }
                  ))}
                  selected={fixedStatuts}
                  locked
                  hint="Rien à choisir ici : ces statuts sont déjà réglés pour ce mail."
                />
              </section>
            )}
            {!isCommerciauxLundi && !fixedStatuts && (
              <div className="kkp-crons-help">
                <Icon icon="mdi:information-outline" width={20} />
                <div>
                  <strong>Pas de filtre par statut</strong>
                  <p className="mb-0">Ce mail ne sélectionne pas les dossiers selon leur statut.</p>
                </div>
              </div>
            )}

            {wizardStep === lastStep && recapBlock}
          </div>
        )}

      </div>

      <div className="kkp-crons-wizard__footer">
        {wizardStep > 0 ? (
          <button
            type="button"
            className="btn btn-outline-secondary kkp-crons-wizard__nav"
            onClick={() => setWizardStep((step) => Math.max(0, step - 1))}
          >
            <Icon icon="mdi:arrow-left" width={18} className="me-1" />
            Étape précédente
          </button>
        ) : lastStep > 0 ? (
          <p className="kkp-crons-wizard__footer-hint mb-0">
            Cliquez sur un type de mail pour continuer.
          </p>
        ) : (
          <span />
        )}
        {wizardStep < lastStep ? (
          <button
            key="wizard-next"
            type="button"
            className="btn btn-primary-600 kkp-crons-wizard__nav"
            disabled={!canGoNext()}
            onClick={() => {
              if (!canGoNext()) return;
              setWizardStep((step) => Math.min(lastStep, step + 1));
            }}
          >
            Étape suivante
            <Icon icon="mdi:arrow-right" width={18} className="ms-1" />
          </button>
        ) : (
          <button
            key="wizard-submit"
            type="submit"
            className={`btn kkp-crons-wizard__nav ${editingId ? 'btn-success-600' : 'btn-primary-600'}`}
            disabled={saving || !canGoNext()}
          >
            {saving
              ? 'Enregistrement…'
              : (editingId ? 'Enregistrer l’alerte' : 'Créer l’alerte')}
          </button>
        )}
      </div>
    </form>
  );
};

export default KkpCronsWizard;
